import React, { useState, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useLanguage } from '../../context/LanguageContext';
import { Modal } from '../ui/Modal';
import { Input, Textarea } from '../ui/Input';
import { Button } from '../ui/Button';

const API_URL = 'https://backend-sites.dathent.com';

export function ContactForm({ isOpen, onClose }) {
  const { t, language } = useLanguage();
  const form = t('forms.contact');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [savedBriefId, setSavedBriefId] = useState(null);

  // Load from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('briefFormData');
    const savedId = localStorage.getItem('savedBriefId');
    
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(prev => ({
          ...prev,
          name: parsed.name || '',
          email: parsed.email || '',
          phone: parsed.phone || ''
        }));
      } catch (e) {
        console.error('Failed to parse saved form data');
      }
    }
    
    if (savedId) {
      setSavedBriefId(savedId);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({ ...prev, phone: value || '' }));
    if (validationErrors.phone) {
      setValidationErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    else if (!isValidPhoneNumber(formData.phone)) {
      errors.phone = 'Invalid phone number';
    }
    if (!formData.message.trim()) errors.message = 'Message is required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Save as brief (contact form is simplified brief)
      const response = await fetch(`${API_URL}/api/brief`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brief_id: savedBriefId || undefined,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          project_type: 'contact',
          description: formData.message,
          language
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Save brief_id to localStorage
        if (result.id) {
          localStorage.setItem('savedBriefId', result.id);
          setSavedBriefId(result.id);
        }
        
        // Update localStorage with contact data
        localStorage.setItem('briefFormData', JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        }));
        
        setStatus({ type: 'success', message: t('forms.success') });
        setTimeout(() => {
          onClose();
          setFormData({ name: '', email: '', phone: '', message: '' });
          setStatus({ type: '', message: '' });
        }, 2000);
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      setStatus({ type: 'error', message: t('forms.error') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6 md:p-8">
        <h2 className="font-heading text-2xl font-bold mb-6">{form.title}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{form.name} *</label>
            <Input 
              name="name" 
              value={formData.name} 
              onChange={handleChange} 
              className={validationErrors.name ? 'border-red-500' : ''}
              data-testid="contact-name-input"
            />
            {validationErrors.name && <p className="text-red-400 text-xs mt-1">{validationErrors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{form.email} *</label>
            <Input 
              name="email" 
              type="email" 
              value={formData.email} 
              onChange={handleChange} 
              className={validationErrors.email ? 'border-red-500' : ''}
              data-testid="contact-email-input"
            />
            {validationErrors.email && <p className="text-red-400 text-xs mt-1">{validationErrors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{form.phone || 'Phone'} *</label>
            <PhoneInput
              international
              defaultCountry="ES"
              value={formData.phone}
              onChange={handlePhoneChange}
              className={`phone-input-wrapper ${validationErrors.phone ? 'border-red-500' : ''}`}
              data-testid="contact-phone-input"
            />
            {validationErrors.phone && <p className="text-red-400 text-xs mt-1">{validationErrors.phone}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{form.message} *</label>
            <Textarea 
              name="message" 
              value={formData.message} 
              onChange={handleChange} 
              className={validationErrors.message ? 'border-red-500' : ''}
              data-testid="contact-message-textarea"
            />
            {validationErrors.message && <p className="text-red-400 text-xs mt-1">{validationErrors.message}</p>}
          </div>

          {status.message && (
            <div className={`p-4 rounded-lg ${status.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
              {status.message}
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
            data-testid="contact-submit-btn"
          >
            {isSubmitting ? '...' : form.submit}
          </Button>
        </form>
      </div>
    </Modal>
  );
}
